import { motion } from "framer-motion";
import { ArrowDown } from "lucide-react";

// Four converging forces creating the market moment \u2014 shown flowing toward
// "Application," WearSpray's proven capability today, not implying every force
// is already fused into the current product.
const FORCES = [
  { label: "Skincare Science", angle: -90 },
  { label: "Wearable Signals", angle: 0 },
  { label: "AI Guidance", angle: 90 },
  { label: "Personal Routines", angle: 180 },
];

const COLORS = ["#00e5ff", "#ff2d78", "#e6c068", "#00e5ff"];
const CX = 100;
const CY = 100;
const RING_R = 64;
const ARC_SPAN = 68;
const LABEL_R = 44;

function polar(cx, cy, r, angleDeg) {
  const rad = (angleDeg * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

function arcPath(centerAngle) {
  const start = polar(CX, CY, RING_R, centerAngle - ARC_SPAN / 2);
  const end = polar(CX, CY, RING_R, centerAngle + ARC_SPAN / 2);
  return `M ${start.x.toFixed(2)} ${start.y.toFixed(2)} A ${RING_R} ${RING_R} 0 0 1 ${end.x.toFixed(2)} ${end.y.toFixed(2)}`;
}

export function WhyNowRing() {
  return (
    <div className="flex flex-col items-center">
      <div
        className="relative aspect-square w-[260px] sm:w-[340px] md:w-[400px] lg:w-[440px] mx-auto"
        data-testid="why-now-ring"
      >
        <svg className="absolute inset-0 w-full h-full" viewBox="0 0 200 200">
          {FORCES.map((f, i) => (
            <motion.path
              key={f.label}
              d={arcPath(f.angle)}
              fill="none"
              stroke={COLORS[i % COLORS.length]}
              strokeWidth="7"
              strokeLinecap="round"
              initial={{ opacity: 0.35 }}
              animate={{ opacity: [0.35, 0.9, 0.35] }}
              transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", delay: i * 0.25 }}
            />
          ))}
        </svg>

        <div className="absolute inset-0 flex items-center justify-center px-12" data-testid="why-now-ring-center">
          <div className="text-center">
            <p className="font-heading text-white text-base sm:text-lg font-medium tracking-tight">WearSpray</p>
            <p className="font-mono text-[9px] sm:text-[10px] tracking-[0.1em] uppercase text-accent mt-1.5">
              The Action Layer
            </p>
          </div>
        </div>

        {FORCES.map((f, i) => {
          const pos = polar(50, 50, LABEL_R, f.angle);
          return (
            <div
              key={f.label}
              className="absolute flex flex-col items-center gap-1 w-20 sm:w-24 text-center"
              style={{ left: `${pos.x}%`, top: `${pos.y}%`, transform: "translate(-50%, -50%)" }}
              data-testid={`why-now-node-${f.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <span
                className="w-2.5 h-2.5 rounded-full shrink-0"
                style={{ backgroundColor: COLORS[i % COLORS.length], boxShadow: `0 0 10px ${COLORS[i % COLORS.length]}` }}
              />
              <span className="font-mono text-[9px] sm:text-[10px] tracking-[0.08em] uppercase text-zinc-300 leading-tight">
                {f.label}
              </span>
            </div>
          );
        })}
      </div>

      <div className="flex flex-col items-center gap-1.5 mt-4" data-testid="why-now-ring-outcome">
        <ArrowDown size={16} className="text-zinc-600" aria-hidden="true" />
        <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-white border border-accent/50 bg-accent/10 rounded-full px-4 py-1.5">
          Application
        </span>
      </div>
    </div>
  );
}
